import { useCallback, useEffect, useRef, useState } from "react";
import api, { formatApiErrorDetail } from "../lib/api";
import logger from "../lib/logger";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function buildWsUrl(courseId) {
  const base = BACKEND_URL.replace(/^http/, "ws");
  return `${base}/api/ws/chat/${courseId}`;
}

/**
 * Course chat hook — owns WebSocket lifecycle, history, and send.
 * Returns { messages, connected, error, send } for the ChatTab component.
 */
export default function useCourseChat(courseId) {
  const [messages, setMessages] = useState([]);
  const [connected, setConnected] = useState(false);
  const [error, setError] = useState("");
  const wsRef = useRef(null);
  const reconnectTimer = useRef(null);

  const loadHistory = useCallback(async () => {
    try {
      const res = await api.get(`/courses/${courseId}/chat`);
      setMessages(res.data);
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  }, [courseId]);

  const openWs = useCallback(() => {
    try {
      const ws = new WebSocket(buildWsUrl(courseId));
      wsRef.current = ws;
      ws.onopen = () => setConnected(true);
      ws.onmessage = (ev) => {
        try {
          const msg = JSON.parse(ev.data);
          setMessages((prev) => (prev.some((m) => m.id === msg.id) ? prev : [...prev, msg]));
        } catch (err) {
          logger.error("Bad WS payload", err);
        }
      };
      ws.onclose = () => {
        setConnected(false);
        if (reconnectTimer.current) clearTimeout(reconnectTimer.current);
        reconnectTimer.current = setTimeout(() => openWs(), 2000);
      };
      ws.onerror = (e) => logger.error("WS error", e);
    } catch (e) {
      logger.error("WS connect failed", e);
    }
  }, [courseId]);

  useEffect(() => {
    loadHistory();
    openWs();
    return () => {
      if (reconnectTimer.current) clearTimeout(reconnectTimer.current);
      if (wsRef.current) {
        wsRef.current.onclose = null; // disable reconnect on unmount
        wsRef.current.close();
      }
    };
  }, [courseId, loadHistory, openWs]);

  const send = useCallback(
    async (text) => {
      const trimmed = text.trim();
      if (!trimmed) return false;
      const ws = wsRef.current;
      if (ws && ws.readyState === WebSocket.OPEN) {
        ws.send(JSON.stringify({ text: trimmed }));
        return true;
      }
      try {
        await api.post("/chat", { course_id: courseId, text: trimmed });
        await loadHistory();
        return true;
      } catch (err) {
        setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
        return false;
      }
    },
    [courseId, loadHistory],
  );

  return { messages, connected, error, send };
}
